import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box,
  Card,
  Chip,
  FormControlLabel,
  Grid,
  Icon,
  LinearProgress,
  Skeleton,
  Switch,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { useUser } from 'components/RDAuthContext';
import { useLocationAdmin } from '../context/LocationAdminContext';
import { useOrgUnitsData, useHierarchyStructure } from '../hooks/useLocationAdminData';
import SystemHealthCard from '../components/SystemHealthCard';
import { metricsService } from '../services/domain';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO, differenceInDays, isSameYear } from 'date-fns';

// Metric Card Component
function MetricCard({ title, value, subtitle, icon, breakdown }) {
  // Use horizontal layout if there are 3 or more breakdown items
  const useHorizontalLayout = breakdown && breakdown.length >= 3;
  
  return (
    <Card sx={{ height: '100%' }}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="button" color="text.secondary" fontWeight="regular" textTransform="capitalize">
              {title}
            </Typography>
            {breakdown ? (
              // Show breakdown by org level
              <Box mt={1}>
                {useHorizontalLayout ? (
                  // Horizontal layout for 3+ items
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    {breakdown.map((item, index) => (
                      <Box key={index} textAlign="center" flex={1}>
                        <Typography variant="h4" fontWeight="bold">
                          {item.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  // Vertical layout for 1-2 items
                  breakdown.map((item, index) => (
                    <Box key={index} display="flex" alignItems="baseline" mb={0.5}>
                      <Typography variant="h3" fontWeight="bold" mr={1}>
                        {item.count}
                      </Typography>
                      <Typography variant="h6" fontWeight="medium" textTransform="capitalize">
                        {item.label}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              <Typography variant="h3" fontWeight="bold" mt={1}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 36,
              minHeight: 36
            }}
          >
            <Icon fontSize="medium" sx={{ color: '#7C3AED' }}>{icon}</Icon>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

// Hierarchy Level Component
function HierarchyLevel({ level, name, count, isActive, hasIssue, icon, levelId }) {
  // Use the level name for count label, pluralized appropriately
  const getCountLabel = () => {
    if (levelId === 'LOCATION') return 'locations';
    
    const nameLower = name.toLowerCase();
    
    // Handle special pluralization cases
    if (nameLower.endsWith('y') && !['ay', 'ey', 'oy', 'uy'].some(v => nameLower.endsWith(v))) {
      // territory → territories, country → countries
      return `${nameLower.slice(0, -1)}ies`;
    } else if (nameLower.endsWith('s') || nameLower.endsWith('x') || nameLower.endsWith('ch')) {
      // areas → areas, complex → complexes, branch → branches
      return nameLower.endsWith('s') ? nameLower : `${nameLower}es`;
    } else {
      // region → regions, district → districts
      return `${nameLower}s`;
    }
  };
  
  const countLabel = getCountLabel();
  
  // Don't show count for Brand level
  const showCount = levelId !== 'BRAND';
  
  return (
    <Card sx={{ mb: 2 }}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Icon sx={{ mr: 1.5, color: '#7C3AED' }}>{icon}</Icon>
            <Box>
              <Typography variant="button" fontWeight="medium">
                {name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {level}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {showCount && (
              <Chip 
                label={`${count} ${countLabel}`}
                size="small" 
                variant="outlined"
                sx={{ 
                  borderColor: 'text.secondary',
                  color: 'text.primary'
                }}
              />
            )}
            <Chip 
              label="Active" 
              size="small" 
              color={isActive ? "success" : "default"}
              variant="outlined"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

// Compact Org Unit Row Component - displays individual org units in a table-like format
function OrgUnitRow({ unit, locationCount, hasManager, showInactiveStatus }) {
  const isInactive = !unit.isActive;
  
  return (
    <Box 
      sx={{ 
        display: 'grid',
        gridTemplateColumns: showInactiveStatus ? '2fr 80px 60px 30px' : '2fr 80px 30px',
        gap: 1,
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        opacity: isInactive ? 0.6 : 1,
        '&:hover': {
          backgroundColor: 'action.hover',
          cursor: 'pointer'
        },
        '&:last-child': {
          borderBottom: 'none'
        }
      }}
    >
      <Typography 
        variant="caption" 
        fontWeight="medium"
        sx={{ 
          color: isInactive ? 'text.disabled' : 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {unit.name}
      </Typography>
      
      <Typography 
        variant="caption" 
        color="text.secondary"
        sx={{ textAlign: 'center' }}
      >
        {locationCount} loc{locationCount !== 1 && 's'}
      </Typography>
      
      {showInactiveStatus && (
        <Typography 
          variant="caption" 
          fontWeight="medium"
          sx={{ 
            textAlign: 'center',
            color: isInactive ? 'text.disabled' : 'success.main'
          }}
        >
          {isInactive ? 'Inactive' : 'Active'}
        </Typography>
      )}
      
      <Box sx={{ textAlign: 'center' }}>
        {hasManager ? (
          <Icon sx={{ color: 'success.main', fontSize: 16 }}>check</Icon>
        ) : (
          <Icon sx={{ color: 'warning.main', fontSize: 16 }}>priority_high</Icon>
        )}
      </Box>
    </Box>
  );
}

// Compact Org Level Overview Card - displays all units at a specific org level in a table format
function OrgLevelOverviewCard({ levelName, levelId, units, icon, onModifyClick }) {
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  
  // Filter units based on active status
  const filteredUnits = showActiveOnly 
    ? units.filter(unit => unit.isActive !== false)
    : units;
  
  const activeCount = units.filter(unit => unit.isActive !== false).length;
  const inactiveCount = units.length - activeCount;
  const unitsWithoutManager = units.filter(unit => !unit.hasManager).length;
  
  return (
    <Card sx={{ height: '100%' }}>
      <Box p={2}>
        {/* Compact Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center">
            <Icon sx={{ mr: 1, color: '#7C3AED', fontSize: 20 }}>{icon}</Icon>
            <Typography variant="button" fontWeight="medium">
              {levelName}s
            </Typography>
            <Chip 
              label={`${units.length} total`}
              size="small" 
              sx={{ 
                ml: 1.5,
                height: 20,
                '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
              }}
            />
          </Box>
          <AdminButton 
            variant="text" 
            size="small" 
            color="dark"
            onClick={onModifyClick}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Modify {levelName}s
          </AdminButton>
        </Box>
        
        {/* Compact Stats Bar */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" gap={1.5}>
            <Typography variant="caption" color="success.main" fontWeight="medium">
              {activeCount} active
            </Typography>
            {inactiveCount > 0 && (
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                {inactiveCount} inactive
              </Typography>
            )}
            {unitsWithoutManager > 0 && (
              <Typography variant="caption" color="warning.main" fontWeight="medium">
                {unitsWithoutManager} need manager
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch 
                checked={!showActiveOnly}
                onChange={(e) => setShowActiveOnly(!e.target.checked)}
                size="small"
                sx={{ p: 0.5 }}
              />
            }
            label={
              <Typography variant="caption" fontWeight="medium">
                All
              </Typography>
            }
            labelPlacement="start"
            sx={{ m: 0, ml: 1 }}
          />
        </Box>
        
        {/* Table Header */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: !showActiveOnly ? '2fr 80px 60px 30px' : '2fr 80px 30px',
            gap: 1,
            alignItems: 'center',
            py: 0.5,
            px: 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover'
          }}
        >
          <Typography variant="caption" fontWeight="bold" color="text.primary">
            Name
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ textAlign: 'center' }}>
            Locations
          </Typography>
          {!showActiveOnly && (
            <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ textAlign: 'center' }}>
              Status
            </Typography>
          )}
          <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ textAlign: 'center' }}>
            Mgr
          </Typography>
        </Box>
        
        {/* Compact List */}
        <Box sx={{ maxHeight: 240, overflowY: 'auto', overflowX: 'hidden' }}>
          {filteredUnits && filteredUnits.length > 0 ? (
            filteredUnits.map((unit, index) => (
              <OrgUnitRow 
                key={unit.id || index} 
                unit={unit}
                locationCount={unit.locationCount || 0}
                hasManager={unit.hasManager}
                showInactiveStatus={!showActiveOnly}
              />
            ))
          ) : (
            <Box p={2} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                No {showActiveOnly ? 'active' : ''} {levelName.toLowerCase()}s
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

// Compact Change Item Component for table display
function ChangeItem({ type, description, user, timestamp, icon }) {
  // Debug log for each change item

  const getIconColor = () => {
    switch(type) {
      case 'move': return 'info.main';
      case 'assign': return 'success.main';
      case 'create': return 'primary.main';
      default: return 'text.secondary';
    }
  };
  
  // Format the timestamp for better UX
  const formatTimestamp = (ts) => {
    if (!ts) return 'Unknown';
    
    try {
      const date = parseISO(ts);
      
      // If today, show relative time
      if (isToday(date)) {
        const distance = formatDistanceToNow(date, { addSuffix: true });
        // Make it more concise
        return distance
          .replace('about ', '')
          .replace('less than ', '<')
          .replace(' minutes', 'm')
          .replace(' minute', 'm')
          .replace(' hours', 'h')
          .replace(' hour', 'h')
          .replace(' seconds', 's')
          .replace(' second', 's');
      }
      
      // If yesterday, show "Yesterday"
      if (isYesterday(date)) {
        return 'Yesterday';
      }
      
      // Within last 7 days, show day name
      const daysAgo = differenceInDays(new Date(), date);
      if (daysAgo < 7) {
        return format(date, 'EEEE'); // Monday, Tuesday, etc.
      }
      
      // Within this year, show month and day
      if (isSameYear(date, new Date())) {
        return format(date, 'MMM d');
      }
      
      // Otherwise show full date
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'grid',
        gridTemplateColumns: '24px 1fr 120px 80px',
        gap: 1.5,
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          backgroundColor: 'action.hover'
        },
        '&:last-child': {
          borderBottom: 'none'
        }
      }}
    >
      <Icon sx={{ fontSize: 18, color: getIconColor() }}>{icon}</Icon>
      <Typography 
        variant="caption" 
        fontWeight="medium"
        sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={description} // Add tooltip for full text
      >
        {description}
      </Typography>
      <Typography 
        variant="caption" 
        color="text"
        sx={{ textAlign: 'right' }}
        title={user} // Add tooltip for full name
      >
        {user}
      </Typography>
      <Typography 
        variant="caption" 
        color="text"
        sx={{ textAlign: 'right' }}
        title={timestamp ? (() => {
          try {
            const date = parseISO(timestamp);
            return format(date, 'PPpp');
          } catch {
            return timestamp;
          }
        })() : ''} // Full date/time on hover
      >
        {formatTimestamp(timestamp)}
      </Typography>
    </Box>
  );
}

function OverviewTab({ 
  onSwitchToStructure,
  onSwitchToUnits,
  onSwitchToLocations,
  onSwitchToHistory
}) {
  const { user } = useUser();
  const renderStartTime = useRef(performance.now());
  const renderCount = useRef(0);
  const [systemHealthData, setSystemHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  
  // Get data from context
  const { 
    locations, 
    orgUnits, 
    counts, 
    recentChanges, 
    systemHealth,
    isLoading 
  } = useLocationAdmin();
  
  // Debug logging for Recent Changes

  // Get hierarchy and org units data (lazy loaded)
  const { data: hierarchyData, isLoading: hierarchyLoading } = useHierarchyStructure();
  const { data: orgUnitsData, isLoading: orgUnitsLoading } = useOrgUnitsData();
  
  // Track renders
  useEffect(() => {
    renderCount.current += 1;
    const renderTime = performance.now() - renderStartTime.current;

    // Log data sizes
    if (renderCount.current === 1) {
    }
  });
  
  // Load system health data
  useEffect(() => {
    const loadHealthData = async () => {
      if (user?.client && !isLoading) {
        try {
          setHealthLoading(true);
          const healthData = await metricsService.getSystemHealth(user.client);
          setSystemHealthData(healthData);
        } catch (error) {
          console.error('Failed to load health data:', error);
        } finally {
          setHealthLoading(false);
        }
      }
    };
    
    loadHealthData();
  }, [user?.client, isLoading]);
  
  // Use context data
  const loading = isLoading || hierarchyLoading;
  
  // Use actual counts from context, but override health metrics with real data if available
  const metrics = useMemo(() => {
    const baseMetrics = counts || {
      totalLocations: 0,
      activeLocations: 0,
      inactiveLocations: 0,
      unknownStatusLocations: 0,
      orgUnits: 0,
      orgUnitBreakdown: [],
      hierarchyHealth: 0,
      issues: 0
    };
    
    // Override with actual system health data if available
    if (systemHealthData) {
      return {
        ...baseMetrics,
        hierarchyHealth: systemHealthData.overallCompletion || 0,
        issues: systemHealthData.issues?.length || 0
      };
    }
    
    return baseMetrics;
  }, [counts, systemHealthData]);
  
  // Build hierarchy levels with proper labels and counts
  const hierarchyLevels = useMemo(() => {
    if (!hierarchyData?.structure) {
      // Return empty while loading
      return [];
    }
    
    // Map the hierarchy structure to include counts
    return hierarchyData.structure.map(level => {
      let count = 0;
      
      // Calculate counts based on level
      if (level.levelId === 'LOCATION') {
        count = locations.length;
      } else if (level.levelId === 'BRAND') {
        count = 1; // Always 1 brand
      } else if (level.levelId.startsWith('ORGLEVEL')) {
        // Count org units at this level
        const levelOrgUnits = orgUnits.filter(unit => unit.level === level.levelId);
        count = levelOrgUnits.length;
      }
      
      // Determine the level descriptor
      let levelDescriptor = level.name || 'Level';
      if (level.levelId === 'BRAND') {
        levelDescriptor = 'Top Level';
      } else if (level.levelId === 'LOCATION') {
        levelDescriptor = 'Base Level';
      } else if (level.name && level.name.startsWith('Level')) {
        // Keep as is for org levels (Level 1, Level 2, Level 3)
        levelDescriptor = level.name;
      }
      
      return {
        level: levelDescriptor,  // "Top Level", "Level 3", "Level 2", "Level 1", "Base Level"
        name: level.label,       // "Brand", "Region", "Area", "Market", "Store"
        count: count,
        isActive: level.isActive,
        icon: level.icon,
        levelId: level.levelId,
        order: level.order
      };
    });
  }, [hierarchyData, locations, orgUnits]);
  
  // Old defaults for fallback
  const defaultHierarchyLevels = [
    { level: 'Top Level', name: 'Brand', count: 1, isActive: true, icon: 'business', levelId: 'BRAND', order: 999 },
    { level: 'Level 3', name: 'Region', count: 4, isActive: true, icon: 'hub', levelId: 'ORGLEVEL_3', order: 3 },
    { level: 'Level 2', name: 'Area', count: 12, isActive: true, icon: 'account_tree', levelId: 'ORGLEVEL_2', order: 2 },
    { level: 'Level 1', name: 'Market', count: 25, isActive: true, icon: 'store_mall_directory', levelId: 'ORGLEVEL_1', order: 1 },
    { level: 'Base Level', name: 'Store', count: 247, isActive: true, icon: 'store', levelId: 'LOCATION', order: 0 }
  ];
  
  // Use org units data from lazy-loaded query or build from context
  const orgUnitsByLevel = orgUnitsData?.orgUnitsByLevel || {
    'ORGLEVEL_3': [
      { id: '1', name: 'Pacific Region', locationCount: 67, hasManager: true },
      { id: '2', name: 'Southwest Region', locationCount: 89, hasManager: true },
      { id: '3', name: 'Mountain Region', locationCount: 45, hasManager: false },
      { id: '4', name: 'Central Region', locationCount: 46, hasManager: true }
    ],
    'ORGLEVEL_2': [
      { id: '5', name: 'Northern California', locationCount: 34, hasManager: true },
      { id: '6', name: 'Southern California', locationCount: 33, hasManager: true },
      { id: '7', name: 'Arizona', locationCount: 45, hasManager: true },
      { id: '8', name: 'Nevada', locationCount: 44, hasManager: false },
      { id: '9', name: 'Colorado', locationCount: 23, hasManager: true },
      { id: '10', name: 'Utah', locationCount: 22, hasManager: true },
      { id: '11', name: 'Kansas', locationCount: 24, hasManager: true },
      { id: '12', name: 'Missouri', locationCount: 22, hasManager: true }
    ],
    'ORGLEVEL_1': [
      { id: '13', name: 'San Francisco', locationCount: 12, hasManager: true },
      { id: '14', name: 'Oakland', locationCount: 11, hasManager: true },
      { id: '15', name: 'San Jose', locationCount: 11, hasManager: false },
      { id: '16', name: 'Los Angeles', locationCount: 17, hasManager: true },
      { id: '17', name: 'San Diego', locationCount: 16, hasManager: true },
      { id: '18', name: 'Phoenix', locationCount: 23, hasManager: true },
      { id: '19', name: 'Tucson', locationCount: 22, hasManager: true },
      { id: '20', name: 'Las Vegas', locationCount: 22, hasManager: false },
      { id: '21', name: 'Reno', locationCount: 22, hasManager: false },
      { id: '22', name: 'Denver', locationCount: 12, hasManager: true },
      { id: '23', name: 'Colorado Springs', locationCount: 11, hasManager: true },
      { id: '24', name: 'Salt Lake City', locationCount: 11, hasManager: true },
      { id: '25', name: 'Provo', locationCount: 11, hasManager: true }
    ]
  };
  
  // Use actual recent changes from context (already fetched)
  // recentChanges is already available from context
  
  // systemHealth is already available from context

  if (loading) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} md={3} key={item}>
              <Card sx={{ height: '100%' }}>
                <Box p={2}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} />
                  <Skeleton variant="text" width="80%" />
                </Box>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: 380 }}>
              <Box p={3}>
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton variant="rectangular" height={250} sx={{ mt: 2 }} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: 380 }}>
              <Box p={3}>
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton variant="rectangular" height={250} sx={{ mt: 2 }} />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Metrics Row */}
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Total Locations"
            value={metrics.totalLocations}
            subtitle={
              <>
                {metrics.activeLocations} active, {metrics.inactiveLocations} closed
                {metrics.unknownStatusLocations > 0 && (
                  <span style={{ color: '#ff9800' }}>
                    {', '}{metrics.unknownStatusLocations} unknown
                  </span>
                )}
              </>
            }
            icon="store"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Total Units"
            value={metrics.orgUnits}
            breakdown={metrics.orgUnitBreakdown}
            icon="business"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Location Hierarchy Health"
            value={`${metrics.hierarchyHealth}%`}
            subtitle={
              <LinearProgress 
                variant="determinate" 
                value={metrics.hierarchyHealth} 
                color={metrics.hierarchyHealth >= 90 ? "success" : metrics.hierarchyHealth >= 70 ? "warning" : "error"}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            }
            icon="health_and_safety"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Issues to Address"
            value={metrics.issues}
            subtitle="Actions required"
            icon="warning_amber"
          />
        </Grid>

        {/* Current Hierarchy Structure */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <Box p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="medium">
                  Current Hierarchy Structure
                </Typography>
                <AdminButton 
                  variant="text" 
                  size="small" 
                  color="dark"
                  onClick={onSwitchToStructure}
                >
                  Modify Structure
                </AdminButton>
              </Box>
              <Typography variant="caption" color="text" display="block" mb={3}>
                Your organizational levels from Brand down to Location
              </Typography>
              
              {hierarchyLevels.map((level, index) => (
                <HierarchyLevel key={index} {...level} />
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Dynamic Org Level Cards - one for each hierarchy level from highest to ORGLEVEL_1 */}
        {hierarchyLevels
          .filter(level => level.levelId && level.levelId.startsWith('ORGLEVEL'))
          .sort((a, b) => b.order - a.order) // Sort by order descending (highest level first)
          .map((level) => (
            <Grid item xs={12} lg={6} key={level.levelId}>
              <OrgLevelOverviewCard
                levelName={level.name}
                levelId={level.levelId}
                units={orgUnitsByLevel[level.levelId] || []}
                icon={level.icon}
                onModifyClick={onSwitchToUnits}
              />
            </Grid>
          ))}

        {/* Recent Changes - Compact Table */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%', minHeight: 400 }}>
            <Box p={3} height="100%" display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="h6" fontWeight="medium">
                  Recent Changes
                </Typography>
                <AdminButton 
                  variant="text" 
                  size="small" 
                  color="dark"
                  onClick={onSwitchToHistory}
                >
                  View All History
                </AdminButton>
              </Box>
              <Typography variant="caption" color="text" display="block" mb={1.5}>
                Latest hierarchy modifications
              </Typography>
              
              {/* Table Header */}
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: '24px 1fr 120px 80px',
                  gap: 1.5,
                  alignItems: 'center',
                  py: 0.5,
                  px: 1,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  backgroundColor: 'action.hover'
                }}
              >
                <Box />
                <Typography variant="caption" fontWeight="bold" color="text">
                  Action
                </Typography>
                <Typography variant="caption" fontWeight="bold" color="text" sx={{ textAlign: 'right' }}>
                  User
                </Typography>
                <Typography variant="caption" fontWeight="bold" color="text" sx={{ textAlign: 'right' }}>
                  When
                </Typography>
              </Box>
              
              {/* Compact Change List */}
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 250 }}>
                {recentChanges && recentChanges.length > 0 ? (
                  recentChanges.slice(0, 5).map((change, index) => (
                    <ChangeItem key={index} {...change} />
                  ))
                ) : (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ p: 2, textAlign: 'center' }}
                  >
                    No recent changes found
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* System Health - Enhanced Component */}
        <Grid item xs={12} lg={6}>
          <SystemHealthCard 
            clientId={user?.client}
            healthData={systemHealthData}
            loading={healthLoading}
            onNavigate={(tab) => {
              // Handle navigation to different tabs
              if (tab === 'locations') {
                onSwitchToLocations && onSwitchToLocations();
              } else if (tab === 'units') {
                onSwitchToUnits && onSwitchToUnits();
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default OverviewTab;